import React from 'react';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';

import track from 'react-tracking';

import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { withUserContext, IUserContext } from './user_context_provider';

import CSPRow, { CSPCardFragment } from './csp_row';
import { CommonsSimilarCspsRow } from '../rql';
import { isEmpty } from 'lodash';
import { isUserInLocalizeCSPCardExperiment, trackLocalizeCSPCardExperiment } from '../seller_location_experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { withGraphql } from '../with_graphql';

const DEFAULT_LIMIT = 12;

const COMPONENT_NAME = 'SimilarCspsRow';

interface Input extends IUserContext {
  title: string;
  cspSlug?: string;
  cspSlugToHide?: string;
  listingId?: string;
  limit?: number;
  onRender?: Function;
  onEmpty?: Function;
  id?: string;
  listingState?: string;
}

type IProps = ChildProps<Input, CommonsSimilarCspsRow.Query>;

export function SimilarCspsRow(props: IProps) {

  const { cspSlug, cspSlugToHide, listingId } = props;
  const loading = !!props.data?.loading;
  const csps = props.data?.cspSearch?.csps || [];
  const limit = props.limit || DEFAULT_LIMIT;

  const hasLookupId = cspSlug || listingId;
  const hasCsps = !loading && !isEmpty(csps);
  const shouldRender = hasLookupId && hasCsps;

  React.useEffect(() => {
    if (shouldRender && props.onRender) props.onRender();
  }, [shouldRender]);

  React.useEffect(() => {
    const hasNoCsps = !loading && isEmpty(csps);
    if (hasNoCsps && props.onEmpty) props.onEmpty();
  }, [loading]);

  React.useEffect(() => {
    if (shouldRender) {
      trackEvent({
        eventName: MParticleEventName.ViewedSimilarCspsRow,
        componentName: COMPONENT_NAME,
        listingState: props.listingState,
        experiments: formatExperimentsForEventAttributes(props.user, [
          ...trackLocalizeCSPCardExperiment(props.user),
        ]),
      });
    }
  }, [shouldRender, props.listingState, props.user]);

  if (!loading && !shouldRender) return null;

  return (
    <CSPRow
      title={props.title}
      csps={csps.filter(c => c.slug !== cspSlugToHide)}
      loading={loading}
      expectedCount={limit}
      hideMetadata
      id={props.id}
      trackingComponentName={COMPONENT_NAME}
      hidePrices={isUserInLocalizeCSPCardExperiment(props.user)}
      shouldTrack
    />
  );
}

const withQuery = withGraphql<IProps, CommonsSimilarCspsRow.Query>(
  gql`
    query Commons_SimilarCspsRow(
      $similarCspSlug: String
      $similarListingId: String
      $limit: Int
    ) {
      cspSearch(input: {
        similarCspSlug: $similarCspSlug
        similarListingId: $similarListingId
        limit: $limit
      }) {
        csps {
          _id
          ...CSPCard
        }
      }
    }
    ${CSPCardFragment},
  `,
  {
    options: (ownProps) => {
      return {
        ssr: false,
        variables: {
          similarCspSlug: ownProps.cspSlug,
          similarListingId: ownProps.listingId,
          limit: ownProps?.limit || DEFAULT_LIMIT,
        },
      };
    },
  },
);

export default track({ componentName: COMPONENT_NAME })(withUserContext<IProps>(withQuery(SimilarCspsRow)));
